import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import AppFooter from '../components/AppFooter';

export default function PrivacyPage() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="privacy-page">
      {/* Header */}
      <div className="bg-white border-b border-beige">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 text-gray-soft hover:text-charcoal hover:bg-cream rounded-lg transition-all"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-sage" />
            <span className="text-lg font-medium text-charcoal">Privacy Policy</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-white rounded-xl border border-beige p-6 sm:p-10 shadow-sm">
            <h1 className="text-2xl sm:text-3xl font-light text-charcoal mb-1">
              Kura Stash Privacy Policy
            </h1>
            <p className="text-sm text-gray-soft mb-8">Last Updated: March 6, 2026</p>

            <div className="space-y-8 text-charcoal leading-relaxed">
              {/* Section 1 */}
              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">1. Introduction</h2>
                <p className="text-sm text-charcoal/80">
                  Welcome to Kura Stash (<a href="https://kurastash.com" className="text-sage hover:text-sage-dark underline">kurastash.com</a>). We are committed to protecting your privacy. This policy explains how we collect, use, and safeguard your information when you use our web application to organize your paper-crafting supplies.
                </p>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">2. Information We Collect</h2>
                <p className="text-sm text-charcoal/80 mb-3">
                  We only collect the minimum information necessary to provide our service:
                </p>
                <div className="space-y-3">
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Account Data</p>
                    <p className="text-sm text-charcoal/80">When you sign in via Google or Facebook, we receive your name, email address, and profile picture. This is used solely to create your account and personalize your experience.</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Stash Data</p>
                    <p className="text-sm text-charcoal/80">We store the information you manually enter about your crafting supplies (e.g., ink pads, dies, stencils).</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Technical Data</p>
                    <p className="text-sm text-charcoal/80">We use Cloudflare to ensure the security and speed of our site. This may involve collecting basic log information like IP addresses to prevent fraud and DDoS attacks.</p>
                  </div>
                </div>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">3. How We Use Your Data</h2>
                <ul className="space-y-2 text-sm text-charcoal/80">
                  <li className="flex gap-2">
                    <span className="text-sage mt-0.5">&#8226;</span>
                    <span>To maintain and manage your user account.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-sage mt-0.5">&#8226;</span>
                    <span>To provide the cataloging and organization features of the app.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-sage mt-0.5">&#8226;</span>
                    <span><strong>Limited Use Requirement:</strong> Kura Stash's use and transfer of information received from Google APIs to any other app will adhere to <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-sage hover:text-sage-dark underline">Google API Services User Data Policy</a>, including the Limited Use requirements.</span>
                  </li>
                </ul>
              </section>

              {/* Section 4 */}
              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">4. Data Sharing &amp; Storage</h2>
                <div className="space-y-3">
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">No Selling of Data</p>
                    <p className="text-sm text-charcoal/80">We do not sell, rent, or trade your personal information to third parties.</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Service Providers</p>
                    <p className="text-sm text-charcoal/80">We use trusted partners like Emergent (hosting) and Cloudflare (CDN/Security) to operate the app. They are bound by confidentiality agreements.</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Data Retention</p>
                    <p className="text-sm text-charcoal/80">We keep your data only as long as your account is active. You may delete your account and all associated data at any time through the app settings.</p>
                  </div>
                </div>
              </section>

              {/* Section 5 */}
              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">5. Your Rights</h2>
                <p className="text-sm text-charcoal/80">
                  You have the right to access, correct, or delete your personal information. If you wish to revoke access to your Google or Facebook account, you can do so through your respective provider's security settings or by contacting us.
                </p>
              </section>

              {/* Section 6 */}
              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">6. Contact Us</h2>
                <p className="text-sm text-charcoal/80">
                  For any questions regarding this policy, please contact us at{' '}
                  <a href="mailto:support@kurastash.com" className="text-sage hover:text-sage-dark underline">
                    support@kurastash.com
                  </a>.
                </p>
              </section>
            </div>
          </div>
        </article>
      </div>

      <AppFooter variant="minimal" />
    </div>
  );
}
